import { resolveModuleRoute } from "./moduleRoutes";

const SAFE_ANCHORS = new Set(["#crm-main-content"]);
const SAFE_TABS = new Set([
  "overview",
  "activity",
  "timeline",
  "details",
  "conversation",
  "workflow",
  "notes",
  "members",
  "connections",
  "permissions",
  "settings",
]);
const SAFE_VIEWS = new Set(["list", "table", "grid", "cards", "overview"]);
const SAFE_MODES = new Set(["standard", "compact", "light", "dark"]);
const INVITATION_ACCEPTANCE_PATH = "/accept-invitation";
const INVITATION_ID_PATTERN = /^inv-[a-z0-9]{24}$/;
const INVITATION_TOKEN_PATTERN = /^[A-Za-z0-9_-]{32,512}$/;

const SAFE_QUERY_RULES = Object.freeze({
  tab: (value) => SAFE_TABS.has(value.toLowerCase()),
  view: (value) => SAFE_VIEWS.has(value.toLowerCase()),
  mode: (value) => SAFE_MODES.has(value.toLowerCase()),
  page: (value) => /^(?:[1-9]\d{0,4})$/.test(value),
  page_size: (value) => ["10", "25", "50", "100"].includes(value),
  direction: (value) => ["asc", "desc"].includes(value.toLowerCase()),
});

const cleanPathname = (pathname) => {
  const value = String(pathname || "/");
  const rooted = value.startsWith("/") ? value : `/${value}`;
  const normalized = rooted.length > 1 ? rooted.replace(/\/+$/, "") : rooted;
  if (normalized === "/") return "/";

  // A one-time invitation is the sole public capability route. Its token is
  // retained only long enough for the acceptance screen to exchange it, then
  // that screen removes it from browser history immediately.
  if (normalized === INVITATION_ACCEPTANCE_PATH) return normalized;

  // Customer and record identifiers never belong in SPA path segments. Only
  // source-controlled module routes (including recognized legacy aliases) are
  // retained; an unknown path returns to the public session entry safely.
  return resolveModuleRoute("", normalized) || "/";
};

/**
 * Return only source-controlled, presentation-only URL state. Account,
 * connection, person, record, search and capability values are deliberately
 * absent from this allowlist and are therefore removed by default.
 */
export function sanitizeBrowserSearch(search = "") {
  const source = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  const safe = new URLSearchParams();

  Object.entries(SAFE_QUERY_RULES).forEach(([key, isValid]) => {
    const values = source.getAll(key);
    if (values.length !== 1) return;
    const value = String(values[0] || "").trim();
    if (isValid(value)) safe.set(key, value);
  });

  const serialized = safe.toString();
  return serialized ? `?${serialized}` : "";
}

export function sanitizeBrowserHash(hash = "") {
  const normalized = String(hash || "");
  return SAFE_ANCHORS.has(normalized) ? normalized : "";
}

export function sanitizedBrowserLocation(location = {}) {
  const pathname = cleanPathname(location.pathname);
  if (pathname === INVITATION_ACCEPTANCE_PATH) {
    const source = new URLSearchParams(String(location.search || "").replace(/^\?/, ""));
    const invitation = source.get("invitation") || "";
    const token = source.get("token") || "";
    const search = (
      source.getAll("invitation").length === 1 &&
      source.getAll("token").length === 1 &&
      INVITATION_ID_PATTERN.test(invitation) &&
      INVITATION_TOKEN_PATTERN.test(token)
    )
      ? `?${new URLSearchParams({ invitation, token }).toString()}`
      : "";
    return { pathname, search, hash: "" };
  }
  return {
    pathname,
    search: sanitizeBrowserSearch(location.search),
    hash: sanitizeBrowserHash(location.hash),
  };
}

export function browserLocationChanged(current, safe) {
  return (
    String(current?.pathname || "/") !== safe.pathname ||
    String(current?.search || "") !== safe.search ||
    String(current?.hash || "") !== safe.hash
  );
}

/**
 * Scrub the initial address before React paints protected content. This is a
 * privacy boundary, not an authorization control; backend APIs still validate
 * every tenant, connection and record identifier they receive.
 */
export function scrubInitialBrowserUrl(browserWindow) {
  const targetWindow = browserWindow || (typeof window !== "undefined" ? window : null);
  if (!targetWindow?.location || !targetWindow?.history?.replaceState) return false;
  const current = targetWindow.location;
  const safe = sanitizedBrowserLocation(current);
  if (!browserLocationChanged(current, safe)) return false;

  targetWindow.history.replaceState(
    targetWindow.history.state,
    "",
    `${safe.pathname}${safe.search}${safe.hash}`
  );
  return true;
}

export function shareablePageUrl(origin, pathname) {
  const safeOrigin = String(origin || "").replace(/\/$/, "");
  return `${safeOrigin}${cleanPathname(pathname)}`;
}
